import React from 'react';
import classnames from 'classnames';

import { Grid, Typography, CircularProgress, ButtonBase } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import ScoreCircle from '@hydra/common/components/ScoreCircle';
import { Image, Translate } from '@hydra/common/components';

import { sendEvent } from '@hydra/utils/gtm';

import placeholderSvg from '@hydra/assets/img/dashboard/placeholder.svg';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(4),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2.5,
    [theme.breakpoints.down('xs')]: {
      display: 'none'
    }
  },
  thumbWrapper: {
    boxShadow: theme.shadows[4],
    borderRadius: theme.shape.borderRadius * 4,
    [theme.breakpoints.down('xs')]: {
      borderRadius: '50%'
    }
  },
  placeholderWrapper: {
    position: 'relative'
  },
  thumbLoader: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1
  },
  siteNameHeading: {
    lineHeight: theme.typography.body1.lineHeight,
    fontWeight: theme.typography.fontWeightSemibold
  },
  siteLogo: {
    borderRadius: theme.shape.borderRadius * 4,
    objectFit: 'cover',
    width: '175px',
    height: '100px',
    [theme.breakpoints.down('xs')]: {
      width: '100px',
      height: '100px',
      minWidth: 'auto'
    }
  },
  scoreCirclePlaceholder: {
    width: 100,
    height: 100,
    border: `4px solid ${theme.palette.grey[200]}`,
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  scoreContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
  scoreTitle: {
    marginRight: theme.spacing(4)
  },
  urlDate: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginLeft: theme.spacing(4)
  }
}));

interface ScreenshotThumbnailProps {
  isLoading?: boolean
  screenshot: string
}

export function ScreenshotThumbnail ({
  screenshot,
  isLoading
}: ScreenshotThumbnailProps) {
  const classes = useStyles();
  return isLoading
    ? (
      <div className={classes.placeholderWrapper}>
        <Image className={classes.siteLogo} src={placeholderSvg} />
        <div className={classes.thumbLoader}>
          <CircularProgress variant='indeterminate' />
        </div>
      </div>
      )
    : (
      <div className={classes.thumbWrapper}>
        <Image className={classes.siteLogo} src={screenshot || placeholderSvg} />
      </div>
      );
}

interface DesktopBannerProps {
  date: string
  score: number | null
  url: string
  animate?: boolean
  scoreHref: string
  screenshot: string
  isLoading: boolean
  className?: string
}

export default function DesktopBanner ({
  date,
  score,
  screenshot,
  url,
  scoreHref,
  isLoading,
  animate = true,
  className: classNameProp
}: DesktopBannerProps) {
  const classes = useStyles();

  const handleTrackClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const value = e.currentTarget.getAttribute('data-value');
    sendEvent(value);
  };

  return (
    <div className={classnames(classes.root, classNameProp)}>
      <Grid
        container
        spacing={3}
        justify='space-between'
        alignItems='center'
      >
        <Grid item sm={8} container wrap='nowrap'>
          <ScreenshotThumbnail isLoading={isLoading} screenshot={screenshot} />
          <Grid item sm={8} container wrap='nowrap' zeroMinWidth className={classes.urlDate}>
            <Typography
              variant='h3'
              noWrap
              className={classes.siteNameHeading}
              title={url}
            >
              {url}
            </Typography>
            <Typography>{date}</Typography>
          </Grid>
        </Grid>
        <Grid item xs={4}>
          <div className={classes.scoreContainer}>
            <div className={classes.scoreTitle}>
              <Typography><Translate content='homepage_score' />:</Typography>
            </div>
            {isLoading
              ? (
                <div className={classes.scoreCirclePlaceholder}>
                  <CircularProgress variant='indeterminate' />
                </div>
                )
              : score !== null && (
                <ButtonBase
                  href={scoreHref}
                  onClick={handleTrackClick}
                  data-value='dashboard-main-score-link'
                >
                  <ScoreCircle
                    showProgress
                    score={score}
                    size='medium'
                    animate={animate}
                    fireworks
                  />
                </ButtonBase>
              )}
          </div>
        </Grid>
      </Grid>
    </div>
  );
}
